import React from 'react';

interface IRCBot {
  nickname: string;
  online: boolean;
  lastSeen: Date | null;
  channel?: string;
}

interface IRCBotCardProps {
  bot: IRCBot;
  isLoading: boolean;
  maintenanceMode?: boolean;
}

const IRCBotCard: React.FC<IRCBotCardProps> = ({ bot, isLoading, maintenanceMode = false }) => {
  const formatLastSeen = (lastSeen: Date | null, isOnline: boolean): string => {
    if (isOnline) return 'Online now';
    if (!lastSeen) return 'Never';
    
    const now = new Date();
    const diffMs = now.getTime() - lastSeen.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  };

  // Check if this bot should be in maintenance mode
  const isInMaintenance = maintenanceMode && ['Banker', 'Dealer', 'Bob', 'DM'].includes(bot.nickname);
  const displayStatus = isInMaintenance ? 'Maintenance' : (bot.online ? 'Online' : 'Offline');
  const statusColor = isInMaintenance ? '#f59e0b' : (bot.online ? '#10b981' : '#ef4444');

  return (
    <div className={`service-card ${bot.online ? 'online' : 'offline'}`}>
      <div className="service-header">
        <div className="service-info">
          <h3 className="service-name">{bot.nickname}</h3>
          <span className="service-category">IRC BOT</span>
        </div>
        <div className="service-status">
          <div 
            className="status-indicator"
            style={{ backgroundColor: statusColor }}
          />
          <span className="status-text">{displayStatus}</span>
        </div>
      </div>

      <div className="service-details">
        <div className="detail-row">
          <span className="detail-label">Channel:</span>
          <span className="service-url">{bot.channel || '#seedpool'}</span>
        </div>
        
        <div className="detail-row">
          <span className="detail-label">Last Seen:</span>
          <span className="last-checked">{formatLastSeen(bot.lastSeen, bot.online)}</span>
        </div>
        
        <div className="detail-row">
          <span className="detail-label">Response Time:</span>
          <span className="response-time">Instant</span>
        </div>
      </div>
      
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default IRCBotCard;
